import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["closeable", "inline", "multiline", "use", "_flexWrapperStyles", "_avatar", "href", "target", "onClick", "onCloseClick", "id", "role", "aria-label", "truncateStringProps", "children"];
import { useRef } from 'react';
import styled from 'foundations-theming/utils';
import { uniqueId } from '../../utils/uniqueId';
import { CloseButton } from '../CloseButton/CloseButton';
import { Clickable } from '../Clickable/Clickable';
import { Link } from '../Link/Link';
import { TruncateString } from '../TruncateString/TruncateString';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { TagProvider } from './TagProvider';
import { useStatusTagContext } from '../StatusTag/StatusTagProvider';
import { deepMerge } from '../../utils/deepMerge';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const backgroundColorMapping = {
  calypso: 'fill-accent-blue-subtle-alt',
  'candy-apple': 'fill-accent-red-subtle-alt',
  heffalump: 'fill-inverse-subtle',
  koala: 'fill-accent-neutral-subtle-alt',
  lorax: 'fill-accent-orange-subtle-alt',
  marigold: 'fill-accent-yellow-subtle-alt',
  norman: 'fill-accent-magenta-subtle-alt',
  olaf: 'icon-core-subtle',
  oz: 'fill-accent-green-subtle-alt',
  pantera: 'fill-accent-neutral-subtle-alt',
  sorbet: 'fill-accent-light-orange-subtle-alt',
  thunderdome: 'fill-accent-purple-subtle-alt',
  /**
   * Custom
   */
  default: 'fill-field-default',
  subtle: 'fill-field-default'
};
const borderColorMapping = {
  calypso: 'border-accent-blue-default',
  'candy-apple': 'border-accent-red-default',
  heffalump: 'fill-accent-neutral-default',
  koala: 'fill-tertiary-default',
  lorax: 'border-accent-orange-default',
  marigold: 'border-caution-default',
  norman: 'border-accent-magenta-default',
  olaf: 'icon-core-subtle',
  oz: 'border-accent-green-default',
  pantera: 'fill-accent-neutral-default',
  sorbet: 'fill-accent-light-orange-default',
  thunderdome: 'border-accent-purple-default',
  default: 'border-core-hover',
  subtle: 'border-core-subtle'
};
const STATUS_ALIAS_MAP = {
  disabled: 'default',
  paused: 'default',
  draft: 'default',
  primary: 'default',
  active: 'success',
  sent: 'success',
  published: 'success',
  inReview: 'warning',
  scheduled: 'warning',
  removed: 'danger',
  cancelled: 'danger',
  error: 'danger'
};
export const USE_COLOR_ALIAS_MAP = {
  // defaults
  disabled: 'default',
  paused: 'default',
  draft: 'default',
  primary: 'default',
  // oz
  success: 'oz',
  active: 'oz',
  sent: 'oz',
  published: 'oz',
  // marigold
  warning: 'marigold',
  inReview: 'marigold',
  scheduled: 'marigold',
  // cand-apple
  danger: 'candy-apple',
  removed: 'candy-apple',
  cancelled: 'candy-apple',
  error: 'candy-apple',
  // calypso
  info: 'calypso'
};
export const getBackgroundColorToken = use => {
  var _backgroundColorMappi;
  return (_backgroundColorMappi = backgroundColorMapping[use]) !== null && _backgroundColorMappi !== void 0 ? _backgroundColorMappi : 'icon-core-default';
};
export const getBorderColorToken = use => {
  var _borderColorMapping;
  return (_borderColorMapping = borderColorMapping[use]) !== null && _borderColorMapping !== void 0 ? _borderColorMapping : 'fill-transparent-default';
};
const getTextColorToken = computedUse => {
  if (computedUse === 'olaf') {
    return 'text-core-on-fill-default';
  }
  if (computedUse === 'subtle') {
    return 'text-core-subtle';
  }
  return 'text-core-default';
};
export const StyledTag = styled.span.withConfig({
  displayName: "Tag__StyledTag",
  componentId: "rtqkfz-0"
})(({
  $computedUse,
  $closeable,
  $inline,
  $cssStyles,
  theme,
  $styleOverrides
}) => {
  const tokenizedBackgroundColor = getBackgroundColorToken($computedUse);
  const tokenizedBorderColor = getBorderColorToken($computedUse);
  const hasExplicitMarginInlineStart = ('marginInlineStart' in $cssStyles);
  const isSubtle = $computedUse === 'subtle';
  const typographyStyle = isSubtle ? 'detail-200' : 'heading-25';
  const {
    fontFamily,
    fontSize,
    fontWeight
  } = theme.typography[typographyStyle];
  const borderRadius = theme.borderRadius['transitional-full-0'];
  const borderColor = theme.color[tokenizedBorderColor];
  return deepMerge(Object.assign({
    fontFamily,
    fontSize,
    fontWeight,
    backgroundColor: theme.color[tokenizedBackgroundColor],
    borderBlockWidth: theme.borderWidth['100'],
    borderInlineWidth: theme.borderWidth['100'],
    borderBlockStyle: 'solid',
    borderInlineStyle: 'solid',
    borderBlockColor: borderColor,
    borderInlineColor: borderColor,
    borderStartStartRadius: borderRadius,
    borderStartEndRadius: borderRadius,
    borderEndEndRadius: borderRadius,
    borderEndStartRadius: borderRadius,
    color: theme.color[getTextColorToken($computedUse)],
    lineHeight: '22px',
    paddingBlock: theme.space['0'],
    paddingInline: theme.space['200'],
    position: 'relative',
    cursor: 'inherit',
    display: 'inline-flex',
    maxInlineSize: '100%',
    overflow: 'hidden',
    userSelect: 'none',
    verticalAlign: $inline ? 'middle' : theme.space['200'],
    paddingInlineEnd: $closeable ? theme.space['700'] : undefined,
    marginBlock: $inline ? theme.space['0'] : undefined,
    marginInline: $inline ? theme.space['100'] : undefined,
    /* Applies spacing between inner elements (ex. text and icon) */
    '& > * + *': {
      marginInlineStart: theme.space['200']
    },
    /* Applies spacing between sibling tags */
    '& + &': {
      marginInlineStart: $inline ? undefined : theme.space['300']
    }
  }, hasExplicitMarginInlineStart && {
    [CSS_SPECIFICITY.OVERRIDE]: {
      marginInlineStart: $cssStyles.marginInlineStart
    }
  }, {
    /* Don't color links in a tag */
    '& a, & a:hover, & a:active': {
      color: 'inherit'
    }
  }, $cssStyles), $styleOverrides);
});
const FlexWrapper = styled.span.withConfig({
  displayName: "Tag__FlexWrapper",
  componentId: "rtqkfz-1"
})(({
  $cssStyles
}) => {
  return Object.assign({
    display: 'inline-flex',
    maxInlineSize: '100%',
    verticalAlign: 'top'
  }, $cssStyles);
});
const ContentWrapper = styled.span.withConfig({
  displayName: "Tag__ContentWrapper",
  componentId: "rtqkfz-2"
})(() => {
  return {
    overflow: 'hidden'
  };
});
const closeButtonStyleOverrides = {
  insetBlockStart: '0',
  insetBlockEnd: '0',
  insetInlineEnd: '6px',
  color: 'currentColor',
  marginBlockStart: 'auto',
  marginBlockEnd: 'auto',
  position: 'absolute',
  transform: 'scale(0.5)'
};
const StyledClickable = styled(Clickable)(({
  theme
}) => {
  return {
    [CSS_SPECIFICITY.OVERRIDE]: {
      fontWeight: theme.typography['heading-200'].fontWeight,
      overflow: 'hidden',
      textDecoration: 'underline'
    }
  };
});
const renderContents = (href, target, onClick, truncatedChildren) => {
  if (href) {
    return /*#__PURE__*/_jsx(Link, {
      href: href,
      onClick: onClick,
      target: target,
      use: "on-bright",
      children: truncatedChildren
    });
  } else if (onClick) {
    return /*#__PURE__*/_jsx(StyledClickable, {
      onClick: onClick,
      children: truncatedChildren
    });
  }
  return /*#__PURE__*/_jsx(ContentWrapper, {
    children: truncatedChildren
  });
};
export const TAG_COMPONENT_NAME = 'UITag';
export const Tag = _ref => {
  var _statusTagContext$tag;
  let {
      closeable = false,
      inline = false,
      multiline = false,
      use = 'default',
      _flexWrapperStyles = {},
      _avatar,
      href,
      target,
      onClick,
      onCloseClick,
      id,
      role,
      'aria-label': ariaLabel,
      truncateStringProps,
      children
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(restProps);
  const containerCssStyles = useMapTokenPropsToCSS(tokenProps);
  const flexWrapperCssStyles = useMapTokenPropsToCSS(_flexWrapperStyles);
  const statusTagContext = useStatusTagContext();
  const theme = useTheme();
  const idRef = useRef(uniqueId('tag-'));
  const computedId = id || idRef.current;
  const computedUse = USE_COLOR_ALIAS_MAP[use] || use;
  const isSubtle = use === 'subtle';
  const linkStyleOverrides = Object.assign({
    overflow: 'hidden'
  }, isSubtle && {
    fontSize: theme.typography['detail-200'].fontSize,
    fontFamily: theme.typography['detail-200'].fontFamily,
    fontWeight: theme.typography['detail-200'].fontWeight
  });
  const truncatedChildren = multiline ? children : /*#__PURE__*/_jsx(TruncateString, Object.assign({
    useFlex: true
  }, truncateStringProps, {
    children: children
  }));
  return /*#__PURE__*/_jsx(StyledTag
  /**
   * "UITag" is intentional to provide backwards compatibility with Selenium helpers
   * @see https://git.hubteam.com/HubSpot/foundations-components/issues/5187
   * @see https://git.hubteam.com/HubSpot/hs-selenium/blob/master/selenium-js-utils/src/UIComponents/select/internal/core.ts#L25
   */, Object.assign({
    "data-component-name": TAG_COMPONENT_NAME,
    "data-tag-use": use,
    id: computedId,
    role: role,
    "aria-label": ariaLabel,
    $computedUse: computedUse,
    $closeable: closeable,
    $inline: inline,
    $cssStyles: containerCssStyles,
    $styleOverrides: (_statusTagContext$tag = statusTagContext === null || statusTagContext === void 0 ? void 0 : statusTagContext.tagStyleOverrides) !== null && _statusTagContext$tag !== void 0 ? _statusTagContext$tag : {}
  }, domProps, callbackProps, {
    children: /*#__PURE__*/_jsxs(TagProvider, {
      closeButtonStyleOverrides: closeButtonStyleOverrides,
      linkStyleOverrides: linkStyleOverrides,
      children: [/*#__PURE__*/_jsxs(FlexWrapper, {
        $cssStyles: flexWrapperCssStyles,
        children: [_avatar, renderContents(href, target, onClick, truncatedChildren)]
      }), closeable && /*#__PURE__*/_jsx(CloseButton, {
        color: "icon-core-default",
        "aria-describedby": computedId,
        onClick: onCloseClick
      })]
    })
  }));
};
Tag.displayName = 'Tag';